import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Spinner, Alert, Button, Badge, Dropdown, Form, Modal } from "react-bootstrap";
import { FaEdit, FaTrash, FaComments, FaFileInvoice, FaStar, FaComment, FaCog, FaChartLine, FaMoneyBillWave, FaClipboardCheck, FaVideo, FaDownload } from "react-icons/fa";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "../CSS/Client.css";
import "./EmailEditor.css";
import EditClientModal from "./EditClientModal";
import ClientPagination from "./ClientPagination";
import ClientFilters from "./ClientFilters";
import ClientMenuModal from "./ClientMenuModal";
import QuickPaymentModal from "./QuickPaymentModal";
import Chatbox from "./Chatbox";
import ChecklistModal from "./ChecklistModal";
import ZohoMeetingModal from "./ZohoMeetingModal";

const ClientManagement = () => {
  const [users, setUsers] = useState([]);
  
  // Quill editor configuration
  const quillRef = useRef(null);
  
  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': ['sans-serif', 'serif', 'monospace'] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }], 
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };
  
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align', 'link'
  ];
  
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", email: "" });
  const [chatUser, setChatUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedMenuUser, setSelectedMenuUser] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [stepFilter, setStepFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [employees, setEmployees] = useState([]);
  const [setShowStepModal] = useState(false);
  const [selectedStepUser, setSelectedStepUser] = useState(null);
  const [selectedStep, setSelectedStep] = useState(2);
  const [userSteps, setUserSteps] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(15);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [showQuickPaymentModal, setShowQuickPaymentModal] = useState(false);
  const [quickPaymentData, setQuickPaymentData] = useState(null);
  const [selectedUserForPayment, setSelectedUserForPayment] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistUser, setChecklistUser] = useState(null);
  const [checklistMemberIndex, setChecklistMemberIndex] = useState(-1);
  const [checklistFormType, setChecklistFormType] = useState("");
  const [checklistMode, setChecklistMode] = useState("preview");
  const [showZohoMeetingModal, setShowZohoMeetingModal] = useState(false);
  const [selectedUserForMeeting, setSelectedUserForMeeting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, employeeFilter, stepFilter, searchQuery, paymentFilter, serviceFilter]);

  const fetchUsers = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch users");

      const data = await response.json();
      const sortedUsers = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setUsers(sortedUsers);
      sortedUsers.forEach((user) => {
        fetchUserSteps(user._id);
        fetchUserMemberSteps(user._id);
      });
      setFilteredUsers(sortedUsers);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch employees");

      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchUserSteps = async (userId) => {
    const token = localStorage.getItem("adminToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/steps`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch user steps");

      const data = await response.json();
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          steps: data.steps
        }
      }));
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchUserMemberSteps = async (userId) => {
    const token = localStorage.getItem("adminToken");
    try {
      const user = users.find(u => u._id === userId);
      if (!user || !user.members || user.members.length === 0) {
        // No members to fetch steps for
        setUserSteps((prev) => ({
          ...prev,
          [userId]: {
            ...prev[userId],
            memberSteps: []
          }
        }));
        return;
      }

      // Fetch steps for all members
      const memberStepsPromises = user.members.map(async (_, memberIndex) => {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/user/${userId}/member-steps/${memberIndex}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          return data.memberSteps || [];
        }
        return [];
      });

      const allMemberSteps = await Promise.all(memberStepsPromises);
      
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          memberSteps: allMemberSteps
        }
      }));
    } catch (error) {
      console.error('Error fetching member steps:', error);
      // Don't set error for member steps as it's not critical
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          memberSteps: []
        }
      }));
    }
  };

  const filterUsers = () => {
    let filtered = users;

    if (employeeFilter !== "all") {
      filtered = filtered.filter((user) => {
        const employee = employees.find((emp) =>
          emp.clients.some((client) => client._id === user._id)
        );
        return employee?._id === employeeFilter;
      });
    }

    if (stepFilter !== "all") {
      filtered = filtered.filter((user) => {
        const steps = userSteps[user._id]?.steps || [];
        const memberSteps = userSteps[user._id]?.memberSteps || [];
        return [...steps, ...memberSteps].some((step) =>
          step.step === parseInt(stepFilter) && step.status === "submitted"
        );
      });
    }

    if (serviceFilter !== "all") {
      filtered = filtered.filter((user) => {
        switch (serviceFilter) {
          case "usa":
            return user.isUSAEnabled;
          case "tax":
            return user.isTaxFilingEnabled;
          case "bookkeeping":
            return user.isBookkeepingEnabled;
          case "cfo":
            return user.isCFOEnabled;
          case "registeredAgent":
            return user.isRegisteredAgentEnabled;
          default:
            return true;
        }
      });
    }

    if (paymentFilter !== "all") {
      filtered = filtered.filter((user) => {
        const hasPaid = user.isUSAEnabled || user.isTaxFilingEnabled ||
          user.isBookkeepingEnabled || user.isCFOEnabled || user.isRegisteredAgentEnabled;
        return paymentFilter === "paid" ? hasPaid : !hasPaid;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((user) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (user.members && user.members.some(m => m.name.toLowerCase().includes(query)))
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const getAssignedEmployee = (userId) => {
    const employee = employees.find((emp) =>
      emp.clients.some((client) => client._id === userId)
    );
    return employee ? employee.name : "-";
  };

  const isNewClient = (createdAt) => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 3);
    return new Date(createdAt) > oneWeekAgo;
  };

  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredUsers.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(filteredUsers.length / clientsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleSelectOne = (userId) => {
    setSelectedIds((prev) => {
      const exists = prev.includes(userId);
      return exists ? prev.filter((id) => id !== userId) : [...prev, userId];
    });
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = currentClients.map((u) => u._id);
    const allSelected = pageIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(selectedIds.filter((id) => !pageIds.includes(id)));
    } else {
      const merged = Array.from(new Set([...selectedIds, ...pageIds]));
      setSelectedIds(merged);
    }
  };

  const openEmailModal = () => setShowEmailModal(true);
  const closeEmailModal = () => setShowEmailModal(false);

  const sendBulkEmail = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one client.");
      return;
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      alert("Subject and message are required.");
      return;
    }
    setSending(true);
    setSendProgress(0);
    
    try {
      const token = localStorage.getItem("adminToken");
      
      // Get user details first to show accurate progress
      const usersResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users-basic-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userIds: selectedIds })
      });
      
      if (!usersResponse.ok) {
        const errorData = await usersResponse.json();
        throw new Error(errorData.message || 'Failed to fetch user details');
      }
      
      const usersData = await usersResponse.json();
      const users = usersData.users || [];
      
      // Send emails one by one to track progress
      const results = [];
      for (let i = 0; i < users.length; i++) {
        const user = users[i];
        
        // Calculate and update progress
        const currentProgress = Math.round(((i) / users.length) * 100);
        setSendProgress(currentProgress);
        
        // Replace $name with user's name
        const personalizedHtml = emailBody.replace(/\$name/g, user.name || 'Client');
        
        // Send email to current user
        const emailResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/send-single-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: user._id,
            subject: emailSubject,
            html: personalizedHtml
          })
        });
        
        const emailResult = await emailResponse.json();
        results.push({
          email: user.email,
          name: user.name,
          status: emailResponse.ok ? 'sent' : 'failed',
          error: !emailResponse.ok ? emailResult.message : undefined
        });
      }
      
      // Set progress to 100% when done
      setSendProgress(100);
      
      // Log the bulk email operation
      const logResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/log-bulk-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userIds: selectedIds,
          subject: emailSubject,
          html: emailBody,
          results: results
        })
      });
      
      // Show success message
      const sentCount = results.filter(r => r.status === 'sent').length;
      setTimeout(() => {
        alert(`Emails sent: ${sentCount}/${results.length}`);
        setShowEmailModal(false);
        // Keep the email form data in case user wants to send similar email to others
        // But don't clear selectedIds so user can continue working with the same selection
        // setEmailSubject("");
        // setEmailBody("");
      }, 500); // Small delay to show 100% progress
      
    } catch (e) {
      alert(e.message);
    } finally {
      setTimeout(() => {
        setSending(false);
        setSendProgress(0);
      }, 1000); // Keep progress bar visible briefly
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/email-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch email logs');
      setEmailLogs(data.items || []);
    } catch (e) {
      setHistoryError(e.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditFormData({ name: user.name, email: user.email });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${selectedUser._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) throw new Error("Failed to update user");

      const updatedUser = await response.json();
      setUsers(users.map((user) =>
        user._id === updatedUser.user._id ? updatedUser.user : user
      ));
      setShowEditModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      const token = localStorage.getItem("adminToken");

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${userId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to delete user");

        setUsers(users.filter((user) => user._id !== userId));
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const toggleChatbox = (user) => {
    setChatUser(chatUser ? null : user);
  };

  const handleMenuClick = (user) => {
    setSelectedMenuUser(user);
    setSelectedStepUser(user);
    setShowMenu(true);
  };

  const handleInvoiceClick = (user) => {
    navigate(`/client/${user._id}/documents`);
  };

  const handleGraphClick = (user) => {
    navigate (`/client/${user._id}/graphs`);
  }

  const handleAnnualReportClick = (user) => {
    navigate (`/client/${user._id}/annual-report`);
  }

  const handleFormAssignment = async (userId, formNumber) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign form");

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCompanyFormAssignment = async (userId, formNumber) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-company-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign company form");

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleMemberFormAssignment = async (userId, memberIndex, formNumber) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-member-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign member form");

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleTaxDateUpdate = async (userId, taxYearEndDate) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/update-tax-dates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, taxYearEndDate }),
        }
      );

      if (!response.ok) throw new Error("Failed to update tax dates");

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      
      // Update selectedMenuUser if it's the same user
      if (selectedMenuUser?._id === userId) {
        setSelectedMenuUser(data.user);
      }
      
      alert("Tax dates updated successfully!");
    } catch (error) {
      setError(error.message);
      alert("Failed to update tax dates: " + error.message);
    }
  };

  const handleMemberTaxDateUpdate = async (userId, memberIndex, taxYearEndDate) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-tax-dates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, taxYearEndDate }),
        }
      );

      if (!response.ok) throw new Error("Failed to update member tax dates");

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      
      // Update selectedMenuUser if it's the same user
      if (selectedMenuUser?._id === userId) {
        setSelectedMenuUser(data.user);
      }
      
      alert("Member tax dates updated successfully!");
    } catch (error) {
      setError(error.message);
      alert("Failed to update member tax dates: " + error.message);
    }
  };

  const handleStepSubmit = async () => {
    if (!selectedStepUser) {
      setError("No user selected for step submission");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            step: selectedStep
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit step");
      }

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      await fetchUserSteps(selectedStepUser._id);
      setShowStepModal(false);
      alert(`Step ${selectedStep} submitted successfully!`);

      if (data.nextStep) {
        alert(`Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const handleMemberStepSubmit = async (memberIndex) => {
    if (!selectedStepUser) {
      setError("No user selected for member step submission");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-member-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            memberIndex,
            step: selectedStep
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit member step");
      }

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      await fetchUserMemberSteps(selectedStepUser._id);
      setShowStepModal(false);
      alert(`Member ${memberIndex + 1} Step ${selectedStep} submitted successfully!`);

      if (data.nextStep) {
        alert(`Member ${memberIndex + 1} Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const isStepSubmitted = (user, step) => {
    if (!user || !userSteps[user._id]?.steps) return false;
    const stepInfo = userSteps[user._id].steps.find(s => s.step === step);
    return stepInfo && stepInfo.status === 'submitted';
  };

  const isMemberStepSubmitted = (user, memberIndex, step) => {
    if (!user || !userSteps[user._id]?.memberSteps?.[memberIndex]) return false;
    const stepInfo = userSteps[user._id].memberSteps[memberIndex].find(s => s.step === step);
    return stepInfo && stepInfo.status === 'submitted';
  };

  const canSubmitStep = (user, step) => {
    if (!user || !userSteps[user._id]?.steps) return false;
    if (step === 1) return true;

    for (let i = 1; i < step; i++) {
      const prevStep = userSteps[user._id].steps.find(s => s.step === i);
      if (!prevStep || prevStep.status !== 'submitted') {
        return false;
      }
    }
    return true;
  };

  const canSubmitMemberStep = (user, memberIndex, step) => {
    if (!user || !userSteps[user._id]?.memberSteps?.[memberIndex]) return false;
    if (step === 1) return true;

    for (let i = 1; i < step; i++) {
      const prevStep = userSteps[user._id].memberSteps[memberIndex].find(s => s.step === i);
      if (!prevStep || prevStep.status !== 'submitted') {
        return false;
      }
    }
    return true;
};

const areAllStepsCompleted = (userId) => {
    if (!userSteps[userId]?.steps) return false;

    const steps = userSteps[userId].steps;
    
    // Check if user completed all 5 steps in the old system
    const completedAllOldSteps = steps.length >= 5 && 
      steps.every(step => step.status === 'submitted' || step.status === 'completed');
    
    if (completedAllOldSteps) return true;
    
    // Check for 4 completed steps in the new system
    const relevantSteps = steps.filter(step => step.step <= 4);
    const completedSteps = relevantSteps.filter(
      step => step.status === 'submitted' || step.status === 'completed'
    ).length;

    return completedSteps >= 4; // Using >= to handle any future cases
};

const areAllMemberStepsCompleted = (userId, memberIndex) => {
    if (!userSteps[userId]?.memberSteps?.[memberIndex]) return false;
    
    const steps = userSteps[userId].memberSteps[memberIndex];
    
    // Check if member completed all 5 steps in the old system
    const completedAllOldSteps = steps.length >= 5 && 
      steps.every(step => step.status === 'submitted' || step.status === 'completed');
    
    if (completedAllOldSteps) return true;
    
    // Check for 4 completed steps in the new system
    const relevantSteps = steps.filter(step => step.step <= 4);
    const completedSteps = relevantSteps.filter(
      step => step.status === 'submitted' || step.status === 'completed'
    ).length;

    return completedSteps >= 4; // Using >= to handle any future cases
};

const renderStepsProgress = (userId, memberIndex = null) => {
    if (!userSteps[userId]) return null;

    // For primary user steps
    if (memberIndex === null) {
      if (areAllStepsCompleted(userId)) {
        return <Badge bg="success">Completed</Badge>;
      }

      const steps = userSteps[userId]?.steps || [];
      const inProgressSteps = steps.filter(step => step.status === "in-progress");

      if (inProgressSteps.length === 0) {
        return <Badge bg="warning">Not Started</Badge>;
      }

      return inProgressSteps.map((step) => (
        <Badge
          key={`primary-${step.step}`}
          bg="danger"
          className="me-1"
        >
          Step {step.step}: {step.status}
        </Badge>
      ));
    }

    // For member steps
    if (userSteps[userId]?.memberSteps?.[memberIndex]) {
      if (areAllMemberStepsCompleted(userId, memberIndex)) {
        return <Badge bg="success">Completed</Badge>;
      }

      const steps = userSteps[userId].memberSteps[memberIndex];
      const inProgressSteps = steps.filter(step => step.status === "in-progress");

      if (inProgressSteps.length === 0) {
        return <Badge bg="warning">Not Started</Badge>;
      }

      return inProgressSteps.map((step) => (
        <Badge
          key={`member-${memberIndex}-${step.step}`}
          bg="danger"
          className="me-1"
        >
          Step {step.step}: {step.status}
        </Badge>
      ));
    }

    return <Badge bg="warning">Not Started</Badge>;
  };

  const renderUSAFormationStepsProgress = (userId) => {
    const user = users.find(u => u._id === userId);
    if (!user || !user.companySteps) {
      return <Badge bg="warning">Not Started</Badge>;
    }

    const completedSteps = user.companySteps.filter(step => step.status === 'submitted').length;
    const totalSteps = user.companySteps.length;

    if (completedSteps === totalSteps) {
      return <Badge bg="success">Completed</Badge>;
    }

    const inProgressSteps = user.companySteps.filter(step => step.status === "in-progress");

    if (inProgressSteps.length === 0) {
      return <Badge bg="warning">Not Started</Badge>;
    }

    return inProgressSteps.map((step) => (
      <Badge
        key={`usa-formation-${step.step}`}
        bg="danger"
        className="me-1"
      >
        Step {step.step}: {step.status}
      </Badge>
    ));
  };

  const handleMemberNameUpdate = async (userId, memberIndex, name) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-name`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, name }),
        }
      );

      if (!response.ok) throw new Error("Failed to update member name");

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const toggleMemberSection = async (userId, isEnabled) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/toggle-member-section`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, isEnabled }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle member section");
      }

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const addMember = async (userId) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/add-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add member");
      }

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      return data.user.members.length - 1; // Return index of new member
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const removeMember = async (userId, memberIndex) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/remove-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove member");
      }

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const toggleServiceLock = async (userId, serviceName, isEnabled) => {
    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/toggle-service`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, serviceName, isEnabled }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle service");
      }

      const data = await response.json();
      
      // Update users list
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      
      // Update selectedMenuUser if it's the same user being modified
      if (selectedMenuUser && selectedMenuUser._id === data.user._id) {
        setSelectedMenuUser(data.user);
      }
      
      // Show success message
      console.log(`Service ${serviceName} ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      
    } catch (error) {
      console.error("Service toggle error:", error);
      setError(error.message);
      
      // Show error alert or notification to user
      alert(`Failed to ${isEnabled ? 'enable' : 'disable'} ${serviceName}: ${error.message}`);
    }
  };

  const handleUSAFormationStepSubmit = async (stepNumber) => {
    if (!selectedStepUser) {
      setError("No user selected for USA Formation step submission");
      return;
    }

    const token = localStorage.getItem("adminToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/admin/submit-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            step: stepNumber
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit USA Formation step");
      }

      const data = await response.json();
      setUsers(users.map((user) =>
        user._id === data.user._id ? data.user : user
      ));
      
      alert(`USA Formation Step ${stepNumber} submitted successfully!`);

      if (data.nextStep) {
        alert(`Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const isUSAFormationStepSubmitted = (userId, stepNumber) => {
    const user = users.find(u => u._id === userId);
    if (!user || !user.companySteps) return false;
    
    const step = user.companySteps.find(s => s.step === stepNumber);
    return step && step.status === 'submitted';
  };

  const canSubmitUSAFormationStep = (userId, stepNumber) => {
    const user = users.find(u => u._id === userId);
    if (!user || !user.companySteps) return false;
    
    // Step 1 can only be submitted by user
    if (stepNumber === 1) return false;
    
    // For steps 2 and 3, check if previous step is submitted
    if (stepNumber > 1) {
      const previousStep = user.companySteps.find(s => s.step === stepNumber - 1);
      if (!previousStep || previousStep.status !== 'submitted') return false;
    }
    
    const currentStep = user.companySteps.find(s => s.step === stepNumber);
    return currentStep && currentStep.status !== 'submitted';
  };

  const handleQuickPayment = (user) => {
    setSelectedUserForPayment(user._id);
    setShowQuickPaymentModal(true);
  };

  const updateQuickPayment = (data) => {
    setQuickPaymentData(data);
  };

  const handleScheduleMeeting = (user) => {
    setSelectedUserForMeeting(null);
  };

  const handleDownloadReport = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/download-users-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'All_Users_Data.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();

    } catch (error) {
      setError(error.message);
      alert('Failed to download report: ' + error.message);
    }
  };

  const handlePreviewChecklist = (user, memberIndex = -1, mode = 'preview') => {
    if (!user) {
      alert('Error: User data is missing');
      return;
    }
    
    const formType = memberIndex === -1 
      ? user.assignedForm 
      : (user.members && user.members[memberIndex] ? user.members[memberIndex].assignedForm : null);
    
    if (!formType) {
      alert('No form assigned. Cannot preview checklist.');
      return;
    }
    
    setChecklistUser(user);
    setChecklistMemberIndex(memberIndex);
    setChecklistFormType(formType);
    setChecklistMode(mode);
    setShowChecklistModal(true);
  };
  
  const handleReviewChecklist = (user, memberIndex = -1) => {
    if (!user) {
      alert('Error: User data is missing');
      return;
    }
    
    const formType = memberIndex === -1 
      ? user.assignedForm 
      : (user.members && user.members[memberIndex] ? user.members[memberIndex].assignedForm : null);
    
    if (!formType) {
      alert('No form assigned. Cannot review checklist.');
      return;
    }
    
    setChecklistUser(user);
    setChecklistMemberIndex(memberIndex);
    setChecklistFormType(formType);
    setChecklistMode('review');
    setShowChecklistModal(true);
  };

  const renderClientRows = () => {
    return currentClients.map((user, index) => {
      const absoluteIndex = indexOfFirstClient + index;

      return (
        <React.Fragment key={user._id}>
          {/* Primary client row - always show */}
          <tr>
            <td>
              <Form.Check
                type="checkbox"
                checked={selectedIds.includes(user._id)}
                onChange={() => toggleSelectOne(user._id)}
              />
            </td>
            <td>{absoluteIndex + 1}</td>
            <td>
              <div className="user-info">
                <span className="full-name">
                  {user.name}
                  {isNewClient(user.createdAt) && (
                    <Badge bg="primary" className="ms-2">New</Badge>
                  )}
                </span>
                <span className="email">{user.email}</span>
              </div>
            </td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td> 
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>
              <div className="action-dropdown">
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                    Actions
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => toggleChatbox(user)}>
                      <FaComments className="me-2" /> Chat
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleInvoiceClick(user)}>
                      <FaFileInvoice className="me-2" /> Documents
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleMenuClick(user)}>
                      <FaCog className="me-2" /> Menu
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleGraphClick(user)}>
                      <FaChartLine className="me-2" /> Bookkeeping
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleAnnualReportClick(user)}>
                      <FaChartLine className="me-2" /> Annual Report Service
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleQuickPayment(user)}>
                      <FaMoneyBillWave className="me-2" /> Quick Payment
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleScheduleMeeting(user)}>
                      <FaVideo className="me-2" /> Schedule Meeting
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleEdit(user)}>
                      <FaEdit className="me-2" /> Edit
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleDelete(user._id)}>
                      <FaTrash className="me-2" /> Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </td>
          </tr>

          {/* Service rows - only show if service is enabled */}
          {[
            { key: 'isUSAEnabled', name: 'USA Company ' },
            { key: 'isTaxFilingEnabled', name: 'Tax Filing' },
            { key: 'isBookkeepingEnabled', name: 'Bookkeeping' },
            { key: 'isCFOEnabled', name: 'CFO Advisory' },
            { key: 'isRegisteredAgentEnabled', name: 'Registered Agent' },
            { key: 'isAnnualReportServiceEnabled', name: 'Annual Report Service' }
          ].map(service => (
            user[service.key] && (
              <tr key={`${user._id}-${service.key}`} className="service-row">
                <td></td>
                <td></td>
                <td>
                  <div className="user-info">
                    <span className="full-name">
                      {user.name} - {service.name}
                    </span>
                  </div>
                </td>
                <td>
                  <Badge bg="success">Paid</Badge>
                </td>
                <td>
                  <span className="employee-assign">
                    {getAssignedEmployee(user._id)}
                  </span>
                </td>
                <td>{service.name}</td>
                <td>
                  {service.key === 'isTaxFilingEnabled' && user.assignedForm ? (
                    <Badge bg="primary">{user.assignedForm}</Badge>
                  ) : (
                    <span className="text-muted">No</span>
                  )}
                </td>
                <td>
                  {user.feedback && user.feedback.rating ? (
                    <div className="star-rating">
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          color={i < user.feedback.rating ? "#ffc107" : "#e4e5e9"}
                        />
                      ))}
                      {user.feedback.comment && (
                        <span
                          className="ms-2 feedback-comment-icon"
                          title={user.feedback.comment}
                          onClick={() => alert(`Feedback: ${user.feedback.comment}`)}
                        >
                          <FaComment />
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">No rating</span>
                  )}
                </td>
                <td>
                  {service.key === 'isTaxFilingEnabled' ? renderStepsProgress(user._id) : 
                   service.key === 'isUSAEnabled' ? renderUSAFormationStepsProgress(user._id) : "-"}
                </td>
                <td>
                  {service.key === 'isTaxFilingEnabled' && user.assignedForm && (
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handlePreviewChecklist(user)}
                    >
                      <FaClipboardCheck className="me-1" /> Preview
                    </Button>
                  )}
                </td>
                <td>
                  {service.key === 'isTaxFilingEnabled' && user.assignedForm && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleReviewChecklist(user)}
                    >
                      <FaClipboardCheck className="me-1" /> Review
                    </Button>
                  )}
                </td>
                <td>-</td>
              </tr>
            )
          ))}

          {/* Member rows if enabled */}
          {user.members && user.members.map((member, memberIndex) => (
            <tr key={`${user._id}-member-${memberIndex}`} className="member-row">
              <td></td>
              <td></td>
              <td>
                <div className="user-info">
                  <span className="full-name">
                    {member.name || `Member ${memberIndex + 1}`}
                  </span>
                </div>
              </td>
              <td>
                <Badge bg="success">Paid</Badge>
              </td>
              <td>
                <span className="employee-assign">
                  {getAssignedEmployee(user._id)}
                </span>
              </td>
              <td>Tax Filing</td>
              <td>
                {member.assignedForm ? (
                  <Badge bg="primary">{member.assignedForm}</Badge>
                ) : (
                  <span className="text-muted">Not assigned</span>
                )}
              </td>
              <td>
                <span className="text-muted">No rating</span>
              </td>
              <td>
                {user.isTaxFilingEnabled ? renderStepsProgress(user._id, memberIndex) : "-"}
              </td>
              <td>
                {user.isTaxFilingEnabled && member.assignedForm && (
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handlePreviewChecklist(user, memberIndex)}
                  >
                    <FaClipboardCheck className="me-1" /> Preview
                  </Button>
                )}
              </td>
              <td>
                {user.isTaxFilingEnabled && member.assignedForm && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleReviewChecklist(user, memberIndex)}
                  >
                    <FaClipboardCheck className="me-1" /> Review
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="admin-dashboard-container">
      <h2 className="dashboard-title mb-4"></h2>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <ClientFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            employeeFilter={employeeFilter}
            setEmployeeFilter={setEmployeeFilter}
            employees={employees}
            stepFilter={stepFilter}
            setStepFilter={setStepFilter}
            paymentFilter={paymentFilter}
            setPaymentFilter={setPaymentFilter}
            serviceFilter={serviceFilter}
            setServiceFilter={setServiceFilter}
          />

          <div className="table-responsive excel-table">
            <Table striped bordered hover className="users-table">
              <thead className="table-header">
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={currentClients.length > 0 && currentClients.every(u => selectedIds.includes(u._id))}
                      onChange={toggleSelectAllOnPage}
                    />
                  </th>
                  <th>Sr No.</th>
                  <th>Client Information</th>
                  <th>P / U</th>
                  <th>Emp.Assign</th>
                  <th>Service Name</th>
                  <th>Form Name</th>
                  <th>Emp.rating (client)</th>
                  <th>Steps WIP</th>
                  <th>Preview Checklist</th>
                  <th>Review Checklist</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {renderClientRows()}
              </tbody>
            </Table>
          </div>

          <br></br>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <Button variant="success" onClick={handleDownloadReport} className="me-2">
                <FaDownload /> Download Report
              </Button>
              <Button variant="info" onClick={openEmailModal} disabled={selectedIds.length === 0}>
                Send Email to Selected ({selectedIds.length})
              </Button>
            </div>
            <Button variant="outline-secondary" onClick={openHistory}>
              View Email History
            </Button>
          </div>

          <ClientPagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={paginate}
            filteredUsers={filteredUsers}
            clientsPerPage={clientsPerPage}
            indexOfFirstClient={indexOfFirstClient}
            indexOfLastClient={indexOfLastClient}
          />
        </>
      )}

      <EditClientModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        selectedUser={selectedUser}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        handleEditSubmit={handleEditSubmit}
      />

      <ClientMenuModal
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        selectedMenuUser={selectedMenuUser}
        setSelectedMenuUser={setSelectedMenuUser}
        selectedStep={selectedStep}
        setSelectedStep={setSelectedStep}
        handleFormAssignment={handleFormAssignment}
        handleMemberFormAssignment={handleMemberFormAssignment}
        handleStepSubmit={handleStepSubmit}
        handleMemberStepSubmit={handleMemberStepSubmit}
        isStepSubmitted={isStepSubmitted}
        isMemberStepSubmitted={isMemberStepSubmitted}
        canSubmitStep={canSubmitStep}
        canSubmitMemberStep={canSubmitMemberStep}
        toggleServiceLock={toggleServiceLock}
        toggleMemberSection={toggleMemberSection}
        handleMemberNameUpdate={handleMemberNameUpdate}
        addMember={addMember}
        removeMember={removeMember}
        userSteps={userSteps}
        handleUSAFormationStepSubmit={handleUSAFormationStepSubmit}
        isUSAFormationStepSubmitted={isUSAFormationStepSubmitted}
        canSubmitUSAFormationStep={canSubmitUSAFormationStep}
        handleCompanyFormAssignment={handleCompanyFormAssignment}
        handleTaxDateUpdate={handleTaxDateUpdate}
        handleMemberTaxDateUpdate={handleMemberTaxDateUpdate}
      />

      <QuickPaymentModal
        showModal={showQuickPaymentModal}
        setShowModal={setShowQuickPaymentModal}
        updateQuickPayment={updateQuickPayment}
        userId={selectedUserForPayment}
      />

      <Modal show={showEmailModal} onHide={closeEmailModal} centered size="lg" className="email-modal">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>Send Email to Selected Clients</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label><strong>Subject</strong></Form.Label>
              <Form.Control
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter subject"
                className="border-primary"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label><strong>Message</strong></Form.Label>
              <div className="personalization-tip">
                <strong>Personalization:</strong> Use <code>$name</code> to automatically insert each client's name in the email.
                <br />Example: "Hello $name, we're writing to inform you..."  
              </div>
              <div className="email-editor-container" style={{ minHeight: '350px' }}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={emailBody}
                  onChange={setEmailBody}
                  modules={modules}
                  formats={formats}
                  style={{ height: '280px' }}
                  placeholder="Type your message (use $name to insert client's name)"
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light border-top">
          {sending && (
            <div className="w-100 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-primary fw-bold">Sending emails...</span>
                <span className="text-primary">{sendProgress}%</span>
              </div>
              <div className="progress" style={{ height: '10px' }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated" 
                  role="progressbar" 
                  style={{ width: `${sendProgress}%` }} 
                  aria-valuenow={sendProgress} 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                ></div>
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div>
              <span className="text-muted">{selectedIds.length} client(s) selected</span>
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                onClick={closeEmailModal} 
                disabled={sending}
                className="me-2"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={sendBulkEmail} 
                disabled={sending}
                className="px-4"
              >
                {sending ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Email Send History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : historyError ? (
            <Alert variant="danger">{historyError}</Alert>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Subject</th>
                    <th>Recipients</th>
                    <th>Sent / Total</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center">No email logs found.</td>
                    </tr>
                  ) : (
                    emailLogs.map((log) => {
                      const sentCount = (log.result || []).filter(r => r.status === 'sent').length;
                      const total = (log.result || []).length;
                      const recipients = (log.recipients || []).join(', ');
                      return (
                        <tr key={log._id}>
                          <td>{new Date(log.createdAt || log.sentAt).toLocaleString()}</td>
                          <td>{log.subject}</td>
                          <td style={{maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={recipients}>{recipients}</td>
                          <td>{sentCount} / {total}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {chatUser && (
        <Chatbox
          user={chatUser}
          onClose={() => setChatUser(null)}
        />
      )}

      <ChecklistModal
        show={showChecklistModal}
        onHide={() => setShowChecklistModal(false)}
        userId={checklistUser?._id}
        memberIndex={checklistMemberIndex}
        formType={checklistFormType}
        mode={checklistMode}
      />

      <ZohoMeetingModal
        show={showZohoMeetingModal}
        onHide={() => setShowZohoMeetingModal(false)}
        userId={selectedUserForMeeting?._id}
        userName={selectedUserForMeeting?.name}
      />
    </div>
  );
};
export default ClientManagement;